import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import isEqual from 'lodash-es/isEqual';

import {
  ActivitiesValidateResponse, ExtraInformationVM, ExtraQuestion, ExtraQuestionDataType, QuestionOptionList
} from '@activities/entities/activities-booking';
import { AccordionType, AccordionSize } from '@design-system/components/templates/accordion/entities/accordion.types';
import { CheckboxFailedType } from '@design-system/components/organisms/checkbox/entities/checkbox.types';
import { InputMask, InputRegex } from '@forms/form-text-input/entities/input-mask';
import { dateValidator } from '@reservation/validators/date.validator';
import { isNullOrUndefined } from '@app/util/util';
import { FormInputType } from '@forms/form-text-input/entities/input-type';
import { INVALID } from '@booking/constants/forms.constants';
import {
  TypographyColor, TypographyFontFamily, TypographyFontSize
} from '@design-system/components/atoms/typography/entities/typography.types';
import { OptionSelected } from '@booking/entities/booking-arrangement';
import { DropDownListPagination } from '@design-system/components/templates/drop-down-list/entities/drop-down-list.types';

@Component({
  selector: 'app-extra-information-form',
  templateUrl: './extra-information-form.component.html',
  styleUrls: ['./extra-information-form.component.sass'],
  standalone: false
})
export class ExtraInformationFormComponent implements OnInit, OnChanges {

  @Input() validation: ActivitiesValidateResponse;
  @Input() info: ExtraInformationVM;
  @Input() key: string;
  @Input() addDynamicElement: UntypedFormArray;
  @Input() position: number
  @Input() isSubmitted = false;
  @Input() isMobile = false;

  public readonly accordionType = AccordionType.Card;
  public readonly accordionSize = AccordionSize.SM;

  public readonly extraQuestionDataType = ExtraQuestionDataType;
  public readonly formInputType = FormInputType;
  public readonly inputMask = InputMask;

  public readonly checkboxFailedType = CheckboxFailedType.Warning;

  public readonly fontSize = TypographyFontSize;
  public readonly fontColor = TypographyColor;
  public readonly fontFamily = TypographyFontFamily

  public dynamicForm: UntypedFormGroup;

  constructor() { }

  ngOnInit(): void {
    this.initForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes?.info &&
      !isEqual(changes?.info?.currentValue, changes?.info?.previousValue) &&
      !isNullOrUndefined(this.addDynamicElement?.at(this.position))
    ) {
      this.createForm();
      this.addDynamicElement?.removeAt(this.position);
      this.addDynamicElement?.insert(this.position, this.dynamicForm);
    }
  }

  public trackById(index: number): number {
    return index;
  }

  public questionHasError(control: string): boolean {
    return this.dynamicForm?.get(control)?.status === INVALID && this.isSubmitted;
  }

  public checkboxIsChecked(control: string): boolean {
    return !isNullOrUndefined(this.dynamicForm?.get(control)?.value);
  }

  public checkboxCheckValue(control: string, checked: boolean): void {
    const value = checked ? true : null;
    this.dynamicForm?.get(control)?.setValue(value);
    this.dynamicForm?.updateValueAndValidity();
  }

  public dropDownValues(optionList: QuestionOptionList[]): string[] {
    return optionList.map(q => q.Value).filter(q => !!q);
  }

  public dropdownIsScrollable(optionList: QuestionOptionList[]): boolean {
    return optionList?.length > DropDownListPagination.DefaultLimit;
  }

  public dropdownSelected(control: string, event: OptionSelected): void {
    this.dynamicForm?.get(control)?.setValue(event.value);
    this.dynamicForm?.updateValueAndValidity();
  }

  private initForm(): void {
    this.createForm();
    this.addDynamicElement?.push(this.dynamicForm);
  }

  private createForm(): void {
    this.dynamicForm = new UntypedFormGroup({
      Key: new UntypedFormControl(this.key),
    });
    this.info?.Questions.forEach(q => {
      this.dynamicForm.addControl(q.FormKey, new UntypedFormControl(null, this.getValidation(q)));
    });
  }

  private getValidation(question: ExtraQuestion): ValidatorFn[] {
    switch (question.DataType) {
      case ExtraQuestionDataType.Text:
        return [Validators.required, Validators.minLength(3)];
      case ExtraQuestionDataType.Numeric:
        return [Validators.required, Validators.minLength(1), Validators.pattern(InputRegex.NUMERIC)];
      case ExtraQuestionDataType.Date:
        return [
          Validators.required, Validators.pattern(InputRegex.DATE), dateValidator()
        ];
      case ExtraQuestionDataType.TrueFalse:
        return [];
      default:
        return [Validators.required];
    }
  }
}
